import React, { useState, useEffect, useRef, useCallback } from "react";
import "./ExpenseTable.css";
import { Link } from "react-router-dom";
import ConfirmSubmitModal from "./ConfirmSubmitModal";
import PaymentPercentInput from "./PaymentPercentInput";
import BankAccountFilter from "./BankAccountFilter";

const SavingsTable = ({ initialFilterStartDate, initialFilterEndDate }) => {
    const [savingsTransactions, setSavingsTransactions] = useState([]);
    const [grantList, setGrantList] = useState([]);
    const [showEditDefaultPaymentPopup, setShowEditDefaultPaymentPopup] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showConfirmedRecords, setShowConfirmedRecords] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const fileInputRef = useRef(null);
    const [customizedCategories, setCustomizedCategories] = useState([]);
    const [savingsTypes, setSavingsTypes] = useState([]);
    const [categoriesBySavingsType, setCategoriesBySavingsType] = useState({});
    const fixedSavingsTypes = [
        'Materials', 'Overhead', 'Travel', 'Fee', 'Equipment',
        'Sub-contract', 'Internal Bank Transfer', 'Credit Card Payment',
        'N/A (Non-grant Item)',
    ];
    const categories = [...new Set([...fixedSavingsTypes, ...savingsTypes])];
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [expensePeriod, setExpensePeriod] = useState("all");
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    const [showCustomPeriodPopup, setShowCustomPeriodPopup] = useState(false);

    // Add these state variables after your existing useState declarations
    const [showEditNotePopup, setShowEditNotePopup] = useState(false);
    const [transactionToEditNote, setTransactionToEditNote] = useState(null);

    // Add state for bank selection
    const [selectedBank, setSelectedBank] = useState("mercury");
    const [availableBankAccounts, setAvailableBankAccounts] = useState([]);
    const [selectedBankAccounts, setSelectedBankAccounts] = useState([]);
    const [showBankColumns, setShowBankColumns] = useState(false);

    // Itemization state
    const [expandedItemRows, setExpandedItemRows] = useState({});
    const toggleItemization = (transactionId) => { setExpandedItemRows(prev => ({ ...prev, [transactionId]: !prev[transactionId] })); };
    const getLineItems = (transaction) => { try { return JSON.parse(transaction["Line Items"] || "[]"); } catch { return []; } };
    const addLineItem = async (transaction) => {
        const items = getLineItems(transaction);
        items.push({ id: Date.now().toString(), name: '', description: '', amount: '', grantCategory: '', customizedCategory: 'N/A', payment: {} });
        await saveLineItems(transaction, items);
    };
    const updateLineItem = async (transaction, itemId, field, value) => {
        const items = getLineItems(transaction);
        const idx = items.findIndex(i => i.id === itemId);
        if (idx === -1) return;
        items[idx][field] = value;
        await saveLineItems(transaction, items);
    };
    const removeLineItem = async (transaction, itemId) => { await saveLineItems(transaction, getLineItems(transaction).filter(i => i.id !== itemId)); };
    const saveLineItems = async (transaction, items) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/savings/${transaction.ID}`,
                { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
                  body: JSON.stringify({ ...transaction, "Line Items": JSON.stringify(items) }) }
            );
            if (response.ok) { setSavingsTransactions(prev => prev.map(t => t.ID === transaction.ID ? { ...t, "Line Items": JSON.stringify(items) } : t)); }
        } catch (error) { console.error("Error saving line items:", error); }
    };

    // Apply initial filter when provided
    useEffect(() => {
        if (initialFilterStartDate && initialFilterEndDate) {
            setCustomStartDate(initialFilterStartDate);
            setCustomEndDate(initialFilterEndDate);
            setExpensePeriod('custom');
            // No need to show popup since we're setting dates programmatically
            setShowCustomPeriodPopup(false);
        }
    }, [initialFilterStartDate, initialFilterEndDate]);

    const handleEditNoteClick = (transaction) => {
        setTransactionToEditNote(transaction);
        setShowEditNotePopup(true);
    };

    const handleSaveNote = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/savings/${transactionToEditNote.ID}`,
                {
                    method: "PUT",
                    credentials: 'include',
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ Note: transactionToEditNote.Note }),
                }
            );

            if (response.ok) {
                // Update the local state to reflect the changes
                const updatedTransactions = filteredTransactions.map((t) =>
                    t.ID === transactionToEditNote.ID
                        ? { ...t, Note: transactionToEditNote.Note }
                        : t
                );
                setFilteredTransactions(updatedTransactions);
                setShowEditNotePopup(false);
            } else {
                throw new Error("Failed to update note");
            }
        } catch (error) {
            console.error("Error updating note:", error);
            alert("Error updating note.");
        }
    };

    // Fetch bank accounts from the company endpoint (using unified /company/banks)
    const fetchBankAccounts = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/company/banks`,
                {
                    credentials: 'include'
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            const banks = data.banks || [];
            const accounts = [];
            
            // Convert flat banks array to account list
            banks.forEach((bank, index) => {
                const bankName = bank.bankName;
                const accountType = bank.accountType || 'Checking';
                const accountNumber = bank.accountNumber || '';
                const lastFour = accountNumber.slice(-4) || accountNumber;
                
                // Only include savings accounts for SavingsTable
                if (accountType.toLowerCase() === 'savings') {
                    accounts.push({
                        bank: bankName,
                        accountNumber: `****${lastFour}`,
                        accountType: accountType,
                        // Lowercased to match the key built per transaction in the filter
                        key: `${bankName.toLowerCase()}|****${lastFour}|savings`
                    });
                }
            });
            
            setAvailableBankAccounts(accounts);
            // Select all accounts by default
            setSelectedBankAccounts(accounts.map(acc => acc.key));
        } catch (error) {
            console.error("Error fetching bank accounts:", error);
        }
    }, []);

    const fetchSavingsTransactions = useCallback(async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/savings`, {
                credentials: 'include'
            });
            const data = await response.json();
            console.log("Savings transactions fetched:", data);
            setSavingsTransactions(data);
        } catch (error) {
            console.error("Error fetching savings transactions:", error);
        }
    }, []);

    const fetchGrantList = useCallback(async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grants/grant-list`, {
                credentials: 'include'
            });
            const data = await response.json();
            setGrantList(data);
        } catch (error) {
            console.error("Error fetching grants:", error);
        }
    }, []);

    const fetchCustomizedCategories = useCallback(async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/customized-categories`, {
                credentials: 'include'
            });
            const data = await response.json();
            setCustomizedCategories(data);
        } catch (error) {
            console.error("Error fetching customized categories:", error);
        }
    }, []);

    const fetchSavingsTypes = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/savings-categories/savings-types`,
                { credentials: 'include' }
            );
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            const data = await response.json();
            setSavingsTypes(data);
        } catch (error) {
            console.error('Error fetching savings types:', error);
        }
    }, []);

    const fetchCategoriesBySavingsType = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/savings-categories/by-savings-type`,
                { credentials: 'include' }
            );
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            const data = await response.json();
            setCategoriesBySavingsType(data);
        } catch (error) {
            console.error('Error fetching categories by savings type:', error);
        }
    }, []);

    const fetchAllTransactions = useCallback(async () => {
        await Promise.all([
            fetchSavingsTransactions(),
            fetchGrantList(),
            fetchCustomizedCategories(),
            fetchBankAccounts(),
            fetchSavingsTypes(),
            fetchCategoriesBySavingsType()
        ]);
    }, [fetchSavingsTransactions, fetchGrantList, fetchCustomizedCategories, fetchBankAccounts, fetchSavingsTypes, fetchCategoriesBySavingsType]);

    useEffect(() => {
        fetchAllTransactions();
    }, [fetchAllTransactions]);

    useEffect(() => {
        filterTransactions();
    }, [
        savingsTransactions,
        showConfirmedRecords,
        expensePeriod,
        customStartDate,
        customEndDate,
        selectedBank,
        selectedBankAccounts
    ]);

    const handleExpensePeriodChange = (event) => {
        const selectedPeriod = event.target.value;
        setExpensePeriod(selectedPeriod);
        if (selectedPeriod === "custom") {
            setShowCustomPeriodPopup(true);
        } else {
            setCustomStartDate("");
            setCustomEndDate("");
        }
    };

    const handleCustomPeriodSubmit = () => {
        setShowCustomPeriodPopup(false);
        setExpensePeriod("custom");
    };

    const filterTransactions = () => {
        let filtered = [...savingsTransactions];

        // Filter out confirmed transactions (same as ExpenseTable logic)
        filtered = filtered.filter((t) => t.Confirmed !== "Yes");

        // Apply period filter logic (same as ExpenseTable)
        if (expensePeriod !== "all") {
            const currentYear = new Date().getFullYear();
            const currentDate = new Date();
            
            if (expensePeriod === "lastYear") {
                const lastYear = currentYear - 1;
                filtered = filtered.filter((t) => {
                    const transactionYear = new Date(t["Date (UTC)"]).getFullYear();
                    return transactionYear === lastYear;
                });
            } else if (expensePeriod === "lastQuarter") {
                const threeMonthsAgo = new Date(currentDate.getTime() - 90 * 24 * 60 * 60 * 1000);
                filtered = filtered.filter((t) => {
                    const transactionDate = new Date(t["Date (UTC)"]);
                    return transactionDate >= threeMonthsAgo && transactionDate <= currentDate;
                });
            } else if (expensePeriod === "lastMonth") {
                const oneMonthAgo = new Date(currentDate.getTime() - 30 * 24 * 60 * 60 * 1000);
                filtered = filtered.filter((t) => {
                    const transactionDate = new Date(t["Date (UTC)"]);
                    return transactionDate >= oneMonthAgo && transactionDate <= currentDate;
                });
            } else if (expensePeriod === "custom" && customStartDate && customEndDate) {
                const startDate = new Date(customStartDate);
                const endDate = new Date(customEndDate);
                filtered = filtered.filter((t) => {
                    const transactionDate = new Date(t["Date (UTC)"]);
                    return transactionDate >= startDate && transactionDate <= endDate;
                });
            } else if (!isNaN(expensePeriod)) {
                // Year filter
                filtered = filtered.filter((t) => {
                    const transactionYear = new Date(t["Date (UTC)"]).getFullYear();
                    return transactionYear === parseInt(expensePeriod);
                });
            }
        }

        // Bank account filtering
        if (selectedBankAccounts.length > 0) {
            filtered = filtered.filter((t) => {
                const bank = (t["Bank Type"] || "Unknown").toLowerCase();
                const accountNumber = t["Last Four Digits"] ? `****${t["Last Four Digits"]}` : "N/A";
                const accountType = "savings";
                const accountKey = `${bank}|${accountNumber}|${accountType}`;
                return selectedBankAccounts.includes(accountKey);
            });
        }

        console.log("filtered transactions:", filtered);

        setFilteredTransactions(filtered);
    };

    const getExpensePeriodOptions = () => {
        const years = [
            ...new Set(
                savingsTransactions.map((t) =>
                    new Date(t["Date (UTC)"]).getFullYear()
                )
            ),
        ];
        return [
            { value: "all", label: "All" },
            ...years.map((year) => ({
                value: year.toString(),
                label: year.toString(),
            })),
            { value: "lastYear", label: "Last Year" },
            { value: "lastQuarter", label: "Last Quarter" },
            { value: "lastMonth", label: "Last Month" },
            { value: "custom", label: "Custom Period" },
        ];
    };

    const handleTransactionChange = (transactionId, field, value) => {
        setSavingsTransactions(prev => 
            prev.map(transaction => 
                transaction.ID === transactionId 
                    ? { ...transaction, [field]: value }
                    : transaction
            )
        );
    };

    const handleCheckTransaction = (transactionId) => {
        handleTransactionChange(transactionId, "Checked", 
            savingsTransactions.find(t => t.ID === transactionId)?.Checked === "true" ? "false" : "true"
        );
    };

    const handleConfirmCheckedRecords = async () => {
        setShowConfirmModal(false);

        const checkedTransactions = savingsTransactions.filter(t => t.Checked === "true");

        if (checkedTransactions.length === 0) {
            alert("Please select transactions to confirm.");
            return;
        }

        // Validate unconfirmed checked transactions
        const errors = [];
        for (const t of checkedTransactions.filter(t => t.Confirmed !== "Yes")) {
            if (["Internal Bank Transfer", "Credit Card Payment", "N/A (Non-grant Item)"].includes(t["Grant Category"])) continue;
            const lineItems = getLineItems(t);
            const desc = `${new Date(t["Date (UTC)"]).toLocaleDateString()} - $${Math.abs(parseFloat(t.Amount)).toFixed(2)}`;
            if (lineItems.length > 0) {
                const itemTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
                const transactionAmount = Math.abs(parseFloat(t.Amount) || 0);
                if (Math.abs(itemTotal - transactionAmount) > 0.01) {
                    errors.push(`${desc}: line item amounts ($${itemTotal.toFixed(2)}) ≠ transaction ($${transactionAmount.toFixed(2)})`);
                }
                for (let i = 0; i < lineItems.length; i++) {
                    if (!lineItems[i].grantCategory) {
                        errors.push(`${desc}: item "${lineItems[i].name || `#${i + 1}`}" has no category assigned`);
                    }
                    const itemPayment = lineItems[i].payment || {};
                    const itemPaymentTotal = grantList.reduce((sum, g) => sum + (parseFloat(itemPayment[g]) || 0), 0);
                    if (Math.abs(itemPaymentTotal - 100) > 0.01) {
                        errors.push(`${desc}: item "${lineItems[i].name || `#${i + 1}`}" grant split = ${itemPaymentTotal.toFixed(1)}% (must be 100%)`);
                    }
                }
            } else {
                const payment = JSON.parse(t["Default Payment"] || "{}");
                const total = grantList.reduce((sum, g) => sum + (parseFloat(payment[g]) || 0), 0);
                if (Math.abs(total - 100) > 0.01) {
                    errors.push(`${desc}: payment total = ${total.toFixed(1)}% (must be 100%)`);
                }
            }
        }
        if (errors.length > 0) {
            alert(`Cannot confirm. The following issues must be fixed:\n\n${errors.join('\n')}`);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/savings/confirmChecked`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ transactions: checkedTransactions }),
            });

            if (response.ok) {
                await fetchSavingsTransactions();
                alert("Checked records confirmed successfully");
            } else {
                alert("Failed to confirm checked records.");
            }
        } catch (error) {
            console.error("Error confirming checked records:", error);
            alert("Error confirming checked records.");
        }
    };

    const handleAutoFill = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/savings/autofill`,
                {
                    method: "POST",
                    credentials: 'include'
                }
            );
            if (response.ok) {
                fetchSavingsTransactions();
                alert("Auto-fill completed successfully");
            } else {
                alert("Failed to auto-fill data.");
            }
        } catch (error) {
            console.error("Error during auto-fill:", error);
            alert("Error during auto-fill.");
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("bankType", selectedBank);

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_API_URL}/savings/import`,
                    {
                        method: "POST",
                        credentials: 'include',
                        body: formData,
                    }
                );

                if (response.ok) {
                    const result = await response.json();
                    
                    // Check if there are duplicates
                    if (result.duplicatesFound && result.duplicatesFound > 0) {
                        const proceed = window.confirm(
                            `${result.duplicatesFound} duplicate transactions found. Do you want to continue and override existing data?`
                        );
                        if (proceed) {
                            const overrideFormData = new FormData();
                            overrideFormData.append("file", file);
                            overrideFormData.append("bankType", selectedBank);
                            overrideFormData.append("override", "true");

                            const overrideResponse = await fetch(
                                `${process.env.REACT_APP_BACKEND_API_URL}/savings/import`,
                                {
                                    method: "POST",
                                    credentials: 'include',
                                    body: overrideFormData,
                                }
                            );

                            if (overrideResponse.ok) {
                                fetchSavingsTransactions();
                                alert("File imported successfully with overrides.");
                            } else {
                                alert("Failed to import file with overrides.");
                            }
                        }
                    } else {
                        fetchSavingsTransactions();
                        alert("File imported successfully.");
                    }
                } else {
                    alert("Failed to import file.");
                }
            } catch (error) {
                console.error("Error importing file:", error);
                alert("Error importing file.");
            }
        }

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleEditDefaultPaymentClick = (transaction) => {
        let defaultPayment = transaction["Default Payment"];

        // If defaultPayment is a string, try to parse it
        if (typeof defaultPayment === "string") {
            try {
                defaultPayment = JSON.parse(defaultPayment);
            } catch (e) {
                defaultPayment = {};
            }
        }

        setSelectedTransaction({
            ...transaction,
            defaultPayment: defaultPayment,
        });
        setShowEditDefaultPaymentPopup(true);
    };

    const handleEditSubmit = async (updatedTransaction) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/savings/${updatedTransaction.ID}`,
                {
                    method: "PUT",
                    credentials: 'include',
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(updatedTransaction),
                }
            );

            if (response.ok) {
                // Auto-unapprove if the transaction was checked
                const originalTransaction = savingsTransactions.find(t => t.ID === updatedTransaction.ID);
                if (originalTransaction && originalTransaction.Checked === "Yes") {
                    try {
                        await fetch(
                            `${process.env.REACT_APP_BACKEND_API_URL}/savings/check/${updatedTransaction.ID}`,
                            {
                                method: "PUT",
                                credentials: 'include',
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ Checked: false }),
                            }
                        );
                    } catch (uncheckError) {
                        console.error("Error auto-unapproving transaction:", uncheckError);
                    }
                }
                fetchSavingsTransactions();
            } else {
                console.error("Failed to update transaction");
            }
        } catch (error) {
            console.error("Error updating transaction:", error);
        }
    };

    const handleEditDefaultPaymentSubmit = async (selectedTransaction) => {
        if (!selectedTransaction) {
            alert("No transaction selected");
            return;
        }

        // Check for grants in the default payment that are not in the current grant list
        const defaultPaymentKeys = Object.keys(
            selectedTransaction.defaultPayment
        );
        const invalidGrants = defaultPaymentKeys.filter(
            (grant) => !grantList.includes(grant)
        );

        // If any invalid grants exist, prompt the user
        if (invalidGrants.length > 0) {
            const confirmOverride = window.confirm(
                `The current Default Payment contains grants (${invalidGrants.join(
                    ", "
                )}) that are not in the current grant list. Editing this record will override the existing items. Do you want to proceed?`
            );

            if (!confirmOverride) {
                return; // If user does not confirm, exit without making changes
            }
        }

        // Validate that the total percentage is 100%
        const totalPercentage = Object.values(
            selectedTransaction.defaultPayment
        ).reduce((sum, percentage) => sum + (parseFloat(percentage) || 0), 0);

        if (Math.abs(totalPercentage - 100) > 0.01) {
            alert(
                `The total percentage of default payment sources must add up to 100%. Current total: ${totalPercentage.toFixed(
                    2
                )}%`
            );
            return;
        }

        try {
            console.log("Sending request to update default payment:", {
                id: selectedTransaction.ID,
                defaultPayment: selectedTransaction.defaultPayment,
            });

            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/savings/defaultPayment/${selectedTransaction.ID}`,
                {
                    method: "PUT",
                    credentials: 'include',
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        id: selectedTransaction.ID,
                        defaultPayment: selectedTransaction.defaultPayment,
                    }),
                }
            );

            if (response.ok) {
                console.log("Default payment updated successfully");
                // Auto-unapprove if the transaction was checked
                const originalTransaction = savingsTransactions.find(t => t.ID === selectedTransaction.ID);
                if (originalTransaction && originalTransaction.Checked === "Yes") {
                    try {
                        await fetch(
                            `${process.env.REACT_APP_BACKEND_API_URL}/savings/check/${selectedTransaction.ID}`,
                            {
                                method: "PUT",
                                credentials: 'include',
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ Checked: false }),
                            }
                        );
                    } catch (uncheckError) {
                        console.error("Error auto-unapproving transaction:", uncheckError);
                    }
                }
                fetchSavingsTransactions();
                setShowEditDefaultPaymentPopup(false);
            } else {
                const errorText = await response.text();
                console.error(
                    "Failed to update default payment. Server response:",
                    errorText
                );
                alert(
                    `Failed to update default payment. Server response: ${errorText}`
                );
            }
        } catch (error) {
            console.error("Error updating default payment:", error);
            alert(`Error updating default payment: ${error.message}`);
        }
    };

    const handleCheckboxChange = async (id, checked) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/savings/check/${id}`,
                {
                    method: "PUT",
                    credentials: 'include',
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ Checked: checked }),
                }
            );
            if (response.ok) {
                fetchSavingsTransactions();
            } else {
                alert("Failed to update check status.");
            }
        } catch (error) {
            console.error("Error updating check status:", error);
            alert("Error updating check status.");
        }
    };

    // Update the handleGrantCategoryChange function
    const handleGrantCategoryChange = (transaction, newCategory) => {
        console.log("New category selected:", newCategory);
        let updatedTransaction = {
            ...transaction,
            "Grant Category": newCategory,
            "Customized Category": [
                "Internal Bank Transfer",
                "Credit Card Payment",
            ].includes(newCategory)
                ? "N/A"
                : transaction["Customized Category"],
        };

        // Filter out "non-grant funds" to check if only one actual grant exists
        const nonGrantFundName =
            grantList.find((grant) =>
                grant.toLowerCase().includes("non-grant")
            ) || "non-grant funds";

        const actualGrants = grantList.filter(
            (grant) => grant.toLowerCase() !== nonGrantFundName.toLowerCase()
        );

        // Create a default payment object
        const newDefaultPayment = {};

        if (newCategory === "N/A (Non-grant Item)") {
            // Set all grant funds to 0%
            grantList.forEach((grant) => {
                newDefaultPayment[grant] = "0";
            });

            // Set 'Non-Grant Funds' to 100%
            newDefaultPayment[nonGrantFundName] = "100";
        } else if (actualGrants.length >= 1) {
            // For any other category, if there's only one actual grant
            // Set the only actual grant to 100% and everything else to 0%
            grantList.forEach((grant) => {
                if (grant === actualGrants[0]) {
                    newDefaultPayment[grant] = "100"; // 100% to the only actual grant
                } else {
                    newDefaultPayment[grant] = "0"; // 0% to all others including non-grant funds
                }
            });
        }

        // Only update the default payment if we're handling one of our special cases
        if (
            newCategory === "N/A (Non-grant Item)" ||
            actualGrants.length >= 1
        ) {
            console.log("Updating default payment:", newDefaultPayment);
            updatedTransaction = {
                ...updatedTransaction,
                "Default Payment": JSON.stringify(newDefaultPayment),
            };
        }

        handleEditSubmit(updatedTransaction);
    };

    return (
        <div className={"expense-table-container"}>
            {/* Same header layout as the checking and credit card tabs. relative z-30
                keeps the Bank Accounts drop-down above the table below it. */}
            <div className="card p-6 relative z-30 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Savings Transactions</h2>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Period</label>
                        <select
                            value={expensePeriod}
                            onChange={handleExpensePeriodChange}
                            className="input-field"
                        >
                            {getExpensePeriodOptions().map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <BankAccountFilter
                        availableAccounts={availableBankAccounts}
                        selectedAccounts={selectedBankAccounts}
                        onSelectionChange={setSelectedBankAccounts}
                        label="Bank Accounts"
                    />
                </div>

                <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                    ref={fileInputRef}
                />

                <div className="flex flex-wrap items-center gap-3">
                    <button
                        onClick={() => setShowBankColumns(!showBankColumns)}
                        className="btn-secondary flex items-center gap-2"
                    >
                        <svg
                            className={`w-4 h-4 transition-transform duration-200 ${showBankColumns ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        {showBankColumns ? 'Hide' : 'Show'} Bank Details
                    </button>

                    <Link to="/customized-deposit-categories">
                        <button className="btn-secondary">
                            Edit Deposit Type/Subcategory
                        </button>
                    </Link>

                    <button
                        onClick={handleAutoFill}
                        className="btn-secondary"
                    >
                        AutoFill
                    </button>

                    <button
                        onClick={() => setShowConfirmModal(true)}
                        className="ml-auto px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                    >
                        Submit Approved Records
                    </button>
                </div>
            </div>

            <div className="card overflow-hidden relative z-0">
                <div className="p-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Savings Account Transactions</h2>
                </div>
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date (UTC)</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                {showBankColumns && (
                                    <>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account #</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    </>
                                )}
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Description</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expense Type</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subcategory</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan={grantList.length || 1}>Payment (%)</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 z-20 bg-gray-50" style={{ boxShadow: '-2px 0 4px rgba(0,0,0,0.05)' }}>Approve</th>
                            </tr>
                            <tr>
                                <th className="px-4 py-2"></th>
                                <th className="px-4 py-2"></th>
                                {showBankColumns && (
                                    <>
                                        <th className="px-4 py-2"></th>
                                        <th className="px-4 py-2"></th>
                                        <th className="px-4 py-2"></th>
                                    </>
                                )}
                                <th className="px-4 py-2"></th>
                                <th className="px-4 py-2"></th>
                                <th className="px-4 py-2"></th>
                                {grantList.map((grant) => (
                                    <th key={grant} className="px-2 py-2 text-center text-xs font-medium text-gray-700 bg-gray-100 border-l border-gray-200">
                                        {grant}
                                    </th>
                                ))}
                                <th className="px-4 py-2"></th>
                                <th className="px-4 py-2 sticky right-0 z-20 bg-gray-50" style={{ boxShadow: '-2px 0 4px rgba(0,0,0,0.05)' }}></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredTransactions.map((transaction, index) => (
                            <React.Fragment key={transaction.ID}>
                                <tr
                                    className={`hover:bg-gray-50 transition-colors duration-150 ${
                                        transaction.Checked === "Yes"
                                            ? "bg-primary-50 border-l-4 border-primary-400"
                                            : (index % 2 === 1 ? "bg-gray-50" : "")
                                    }`}
                                >
                                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                        {new Date(
                                            transaction["Date (UTC)"]
                                        ).toLocaleDateString()}
                                    </td>
                                    <td
                                        className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                                            parseFloat(transaction.Amount) < 0
                                                ? "text-red-600"
                                                : "text-green-600"
                                        }`}
                                    >
                                        $
                                        {Math.abs(
                                            parseFloat(transaction.Amount)
                                        ).toFixed(2)}
                                    </td>
                                    {showBankColumns && (
                                        <>
                                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{transaction["Bank Type"] || "Unknown"}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{transaction["Account Number"] || "N/A"}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">Savings</td>
                                        </>
                                    )}
                                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={transaction["Bank Description"]}>{transaction["Bank Description"]}</td>
                                    <td className="px-4 py-3">
                                        {getLineItems(transaction).length > 0 ? (
                                            <span className="text-xs text-gray-400">—</span>
                                        ) : (
                                        <select
                                            className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                            value={transaction["Grant Category"]}
                                            onChange={(e) =>
                                                handleGrantCategoryChange(
                                                    transaction,
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="">
                                                Select Category
                                            </option>
                                            {categories.map((category) => (
                                                <option
                                                    key={category}
                                                    value={category}
                                                >
                                                    {category}
                                                </option>
                                            ))}
                                        </select>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {getLineItems(transaction).length > 0 ? (
                                            <span className="text-xs text-gray-400">—</span>
                                        ) : (
                                        <select
                                            className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
                                            value={
                                                transaction["Customized Category"]
                                            }
                                            onChange={(e) =>
                                                handleEditSubmit({
                                                    ...transaction,
                                                    "Customized Category":
                                                        e.target.value,
                                                })
                                            }
                                            disabled={[
                                                "Internal Bank Transfer",
                                                "Credit Card Payment",
                                            ].includes(
                                                transaction["Grant Category"]
                                            )}
                                        >
                                            <option value="N/A">N/A</option>
                                            {(() => {
                                                const selectedSavingsType = transaction["Grant Category"] || '';
                                                const typeSpecific = categoriesBySavingsType[selectedSavingsType] || [];
                                                const merged = [...new Set(typeSpecific)].filter(c => c !== 'N/A');
                                                return merged.map((category) => (
                                                    <option key={category} value={category}>{category}</option>
                                                ));
                                            })()}
                                        </select>
                                        )}
                                    </td>
                                    {grantList.map((grant) => {
                                        const payment = JSON.parse(transaction["Default Payment"] || "{}");
                                        const percentage = payment[grant] || "0";
                                        const isItemized = getLineItems(transaction).length > 0;
                                        return (
                                            <td key={grant} className="px-2 py-3 text-center text-sm border-l border-gray-200">
                                                {isItemized ? <span className="text-gray-400">—</span> : `${percentage}%`}
                                            </td>
                                        );
                                    })}
                                    <td className="px-4 py-3">
                                        <div className="flex flex-col gap-1">
                                            <button
                                                className="text-xs px-2 py-1 bg-primary-50 text-primary-700 rounded hover:bg-primary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                onClick={() =>
                                                    handleEditDefaultPaymentClick(
                                                        transaction
                                                    )
                                                }
                                                disabled={[
                                                    "Internal Bank Transfer",
                                                    "Credit Card Payment",
                                                ].includes(
                                                    transaction["Grant Category"]
                                                )}
                                            >
                                                Edit Payment
                                            </button>
                                            <button
                                                className={`text-xs px-2 py-1 rounded transition-colors ${transaction.Note ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                                                onClick={() => handleEditNoteClick(transaction)}
                                                title={transaction.Note ? transaction.Note : 'Add note'}
                                            >
                                                {transaction.Note ? 'Edit Note' : 'Add Note'}
                                            </button>
                                            <button
                                                className={`text-xs px-2 py-1 rounded transition-colors ${getLineItems(transaction).length > 0 ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                                                onClick={() => toggleItemization(transaction.ID)}
                                            >
                                                {getLineItems(transaction).length > 0 ? `Items (${getLineItems(transaction).length})` : 'Itemize'}
                                            </button>
                                        </div>
                                    </td>
                                    <td className={`px-4 py-3 text-center sticky right-0 z-10 ${transaction.Checked === "Yes" ? "bg-primary-50" : (index % 2 === 1 ? "bg-gray-50" : "bg-white")}`} style={{ boxShadow: '-2px 0 4px rgba(0,0,0,0.05)' }}>
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                            checked={transaction.Checked === "Yes"}
                                            onChange={(e) =>
                                                handleCheckboxChange(
                                                    transaction.ID,
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    </td>
                                </tr>
                                {/* Line Items sub-rows */}
                                {expandedItemRows[transaction.ID] && (
                                    <>
                                        {getLineItems(transaction).map((item, itemIdx) => {
                                            const itemPayment = item.payment || {};
                                            const itemPaymentTotal = grantList.reduce((sum, g) => sum + (parseFloat(itemPayment[g]) || 0), 0);
                                            return (
                                            <tr key={item.id} className="bg-blue-50 border-b border-blue-100">
                                                <td className="pl-8 pr-2 py-1.5 text-xs text-gray-500">
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-gray-400">#{itemIdx + 1}</span>
                                                        <input type="text" className="w-24 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                            defaultValue={item.name || ''} placeholder="Name"
                                                            onBlur={(e) => updateLineItem(transaction, item.id, 'name', e.target.value)} />
                                                    </div>
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <input type="number" step="0.01" className="w-20 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                        defaultValue={item.amount} placeholder="Amount"
                                                        onBlur={(e) => updateLineItem(transaction, item.id, 'amount', e.target.value)} />
                                                </td>
                                                {showBankColumns && (<><td></td><td></td><td></td></>)}
                                                <td className="px-2 py-1.5">
                                                    <select className="w-24 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                        value={item.grantCategory || ''} onChange={(e) => updateLineItem(transaction, item.id, 'grantCategory', e.target.value)}>
                                                        <option value="">Select</option>
                                                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <select className="w-24 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                        value={item.customizedCategory || 'N/A'} onChange={(e) => updateLineItem(transaction, item.id, 'customizedCategory', e.target.value)}>
                                                        <option value="N/A">N/A</option>
                                                    </select>
                                                </td>
                                                {grantList.map(grant => (
                                                    <td key={grant} className="px-1 py-1.5 text-center border-l border-blue-100">
                                                        <PaymentPercentInput
                                                            inputKey={`${item.id}-${grant}-${itemPayment[grant] || "0"}`}
                                                            grant={grant}
                                                            grants={grantList}
                                                            payment={itemPayment}
                                                            onChange={(newPayment) => updateLineItem(transaction, item.id, 'payment', newPayment)}
                                                        />
                                                    </td>
                                                ))}
                                                <td className="px-2 py-1.5">
                                                    <div className="flex items-center gap-1">
                                                        {Math.abs(itemPaymentTotal - 100) > 0.01 && itemPaymentTotal > 0 && (
                                                            <span className="text-red-500 text-xs font-medium">{itemPaymentTotal.toFixed(0)}%</span>
                                                        )}
                                                        <button onClick={() => removeLineItem(transaction, item.id)} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
                                                    </div>
                                                </td>
                                                <td></td>
                                            </tr>
                                            );
                                        })}
                                        <tr className="bg-blue-50" style={{ borderBottom: '2px solid #93c5fd' }}>
                                            <td colSpan={20} className="pl-8 pr-4 py-2">
                                                <button onClick={() => addLineItem(transaction)}
                                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                    </svg>
                                                    Add Line Item
                                                </button>
                                            </td>
                                        </tr>
                                    </>
                                )}
                            </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Custom Period Popup */}
            {showCustomPeriodPopup && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <h3>Select Custom Period</h3>
                        <div>
                            <label>Start Date: </label>
                            <input
                                type="date"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                            />
                        </div>
                        <div>
                            <label>End Date: </label>
                            <input
                                type="date"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                            />
                        </div>
                        <div className="popup-buttons">
                            <button onClick={handleCustomPeriodSubmit}>Apply</button>
                            <button onClick={() => setShowCustomPeriodPopup(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Note Editing Popup */}
            {showEditNotePopup && transactionToEditNote && (
              <div className="popup-overlay">
                <div className="popup">
                  <h3>Edit Transaction Note</h3>
                  <div className="popup-content">
                    <div className="transaction-details">
                      <p><strong>Date:</strong> {new Date(transactionToEditNote['Date (UTC)']).toLocaleDateString()}</p>
                      <p><strong>Description:</strong> {transactionToEditNote['Bank Description']}</p>
                      <p><strong>Amount:</strong> ${Math.abs(parseFloat(transactionToEditNote.Amount)).toFixed(2)}</p>
                    </div>
                    <div className="note-textarea-container">
                      <label>Note:</label>
                      <textarea
                        value={transactionToEditNote.Note || ''}
                        onChange={(e) => setTransactionToEditNote({
                          ...transactionToEditNote,
                          Note: e.target.value
                        })}
                        placeholder="Add your notes here..."
                        rows={4}
                      />
                    </div>
                    <div className="popup-buttons">
                      <button onClick={handleSaveNote}>Save Note</button>
                      <button onClick={() => setShowEditNotePopup(false)}>Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Default Payment Popup */}
            {showEditDefaultPaymentPopup && selectedTransaction && (
                <div className={"popup-overlay"}>
                    <EditDefaultPaymentPopup
                        selectedTransaction={selectedTransaction}
                        grantList={grantList}
                        onSave={handleEditDefaultPaymentSubmit}
                        onCancel={() => setShowEditDefaultPaymentPopup(false)}
                    />
                </div>
            )}

            <ConfirmSubmitModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmCheckedRecords}
            />
        </div>
    );
};

const EditDefaultPaymentPopup = ({
    selectedTransaction,
    grantList,
    onSave,
    onCancel,
}) => {
    const [defaultPayment, setDefaultPayment] = useState(
        typeof selectedTransaction.defaultPayment === "string"
            ? JSON.parse(selectedTransaction.defaultPayment || "{}")
            : selectedTransaction.defaultPayment || {}
    );

    const handlePercentageChange = (grant, percentage) => {
        setDefaultPayment((prev) => ({
            ...prev,
            [grant]: percentage,
        }));
    };

    const handleSubmit = () => {
        const totalPercentage = Object.values(defaultPayment).reduce(
            (sum, value) => sum + parseFloat(value || 0),
            0
        );

        if (Math.abs(totalPercentage - 100) > 0.01) {
            alert(
                `Total percentage must equal 100%. Current total: ${totalPercentage.toFixed(
                    2
                )}%`
            );
            return;
        }
        onSave({ ...selectedTransaction, defaultPayment });
    };

    return (
        <div className="popup">
            <h3>Edit Payment</h3>
            {grantList.map((grant) => (
                <div key={grant}>
                    <label>{grant}</label>
                    <input
                        type="number"
                        value={defaultPayment[grant] || ""}
                        onChange={(e) =>
                            handlePercentageChange(grant, e.target.value)
                        }
                        min="0"
                        max="100"
                        step="0.01"
                    />
                </div>
            ))}
            <button onClick={handleSubmit}>Save</button>
            <button onClick={onCancel}>Cancel</button>
        </div>
    );
};

export default SavingsTable;